import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/AppShell";
import { useState } from "react";
import { Activity, Palette, SlidersHorizontal, Wrench, Zap, type LucideIcon } from "lucide-react";
import { AppearanceSettingsModal } from "@/components/configuracion/AppearanceSettingsModal";
import { IntegrationsSettingsModal } from "@/components/configuracion/IntegrationsSettingsModal";
import { AuditSettingsModal } from "@/components/configuracion/AuditSettingsModal";
import { PerfilamientoSettingsModal } from "@/components/configuracion/PerfilamientoSettingsModal";
import { SistemaSettingsModal } from "@/components/configuracion/SistemaSettingsModal";

export const Route = createFileRoute("/configuracion")({
  head: () => ({ meta: [{ title: "Configuración — LicitaPro" }] }),
  component: Page,
});

type SectionId = "apariencia" | "integraciones" | "auditoria" | "perfilamiento" | "sistema";

const SECTIONS: {
  id: SectionId;
  icon: LucideIcon;
  title: string;
  description: string;
}[] = [
  {
    id: "apariencia",
    icon: Palette,
    title: "Apariencia",
    description: "Tema, color principal, tamaño de fuente y densidad de la interfaz.",
  },
  {
    id: "integraciones",
    icon: Zap,
    title: "Integraciones",
    description: "Envía las alertas a Slack, Telegram o Microsoft Teams.",
  },
  {
    id: "auditoria",
    icon: Activity,
    title: "Auditoría",
    description: "Revisa las últimas acciones registradas en el sistema.",
  },
  {
    id: "perfilamiento",
    icon: SlidersHorizontal,
    title: "Perfilamiento",
    description: "Umbral de compatibilidad y límite de licitaciones analizadas.",
  },
  {
    id: "sistema",
    icon: Wrench,
    title: "Sistema",
    description: "Mantención interna de los datos de Mercado Público (recuperar un día).",
  },
];

function Page() {
  const [activeModal, setActiveModal] = useState<SectionId | null>(null);
  const close = (open: boolean) => !open && setActiveModal(null);

  return (
    <AppShell title="Configuración" subtitle="Ajustes del sistema y preferencias">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveModal(s.id)}
            className="group relative text-left rounded-2xl border border-border bg-card p-6 shadow-elegant transition-all hover:-translate-y-0.5 hover:border-border-strong hover:shadow-lg"
          >
            <div className="size-14 rounded-xl bg-primary-soft grid place-items-center">
              <s.icon className="size-7 text-primary" />
            </div>
            <h3 className="mt-4 text-lg font-display font-semibold text-foreground group-hover:text-primary transition-colors">
              {s.title}
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground">{s.description}</p>
          </button>
        ))}
      </div>

      <AppearanceSettingsModal open={activeModal === "apariencia"} onOpenChange={close} />
      <IntegrationsSettingsModal open={activeModal === "integraciones"} onOpenChange={close} />
      <AuditSettingsModal open={activeModal === "auditoria"} onOpenChange={close} />
      <PerfilamientoSettingsModal open={activeModal === "perfilamiento"} onOpenChange={close} />
      <SistemaSettingsModal open={activeModal === "sistema"} onOpenChange={close} />
    </AppShell>
  );
}
